<template>
  <div class="cities container">
    <h2 class="cities__title">{{ $t('homepage.city_select.title') }}</h2>

    <SButton v-if="$grid.breakpoint === 'xs'" class="cities__cta centered" @click="toggleCityModal"
      >{{ $t('homepage.city_select.button') }}
    </SButton>
    <ModalWrapper
      v-if="$grid.breakpoint === 'xs'"
      transitionName="slide-left"
      :isVisible="showCityModal"
      :steps="steps"
      :show-over-flow="false"
      @close="toggleCityModal"
    />
    <MultipleCitiesSelection
      v-if="$grid.breakpoint !== 'xs'"
      :categories="filteredCategories"
      @cities-selected="(val) => setSelectedCities(val)"
    />
    <SButton v-if="$grid.breakpoint !== 'xs'" class="cities__cta centered" @click="redirectToFilterdEvents()"
      >{{ $t('homepage.city_select.show_citie') }}
    </SButton>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useCategoryStore } from '@/stores/category';

const SButton = defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue'));
const ModalWrapper = defineAsyncComponent(() => import('@/components/molecules/modals/ModalWrapper.vue'));
const MultipleCitiesSelection = defineAsyncComponent(
  () => import('@/components/Pages/Homepage/children/MultipleCitiesSelectorUnstyledList.vue')
);
const { $i18n, $router, $grid } = useNuxtApp();
const categoriesUrl = ref(null);
const showCityModal = ref(false);
const categoryStore = useCategoryStore();
const { categories } = storeToRefs(categoryStore);

function toggleCityModal() {
  showCityModal.value = !showCityModal.value;
}

const filteredCategories = computed(() => {
  return categories.value.filter(
    (category: Category) =>
      (category.is_city === 1 && category.enabled === 1 && category.productCount > 0) ||
      (category.slug === 'online-events' && category.productCount > 0)
  );
});

const isSingleCity = ref(false);

function setSelectedCities(val) {
  if (val.length === 1) {
    isSingleCity.value = true;
    return (categoriesUrl.value = val[0].slug);
  }
  isSingleCity.value = false;
  return (categoriesUrl.value = val
    .map((city) => `mainTaxonCodes=${city.slug}`)
    .join(',')
    .replaceAll(',', '&'));
}

function redirectToFilterdEvents() {
  if (!categoriesUrl.value) return;
  if (isSingleCity.value) {
    return $router.push(`${categoriesUrl.value}`);
  }
  return $router.push(`events?${categoriesUrl.value}`);
}

const emit = defineEmits(['close', 'citySelected']);

const steps = computed(() => {
  return [
    {
      title: $i18n.t('homepage.city_select.title'),
      content: {
        component: defineAsyncComponent(
          () => import('@/components/Pages/Homepage/children/MultipleCitiesSelectorUnstyledList.vue')
        ),
        props: {
          categories: filteredCategories.value
        },
        events: {
          ['cities-selected']: (cat) => {
            setSelectedCities(cat);
          }
        }
      },
      primaryAction: {
        component: defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue')),
        props: {
          text: $i18n.t('homepage.city_select.close'),
          outlined: true,
          style: 'border:none;'
        },
        events: {
          click: () => toggleCityModal()
        }
      },
      secondaryAction: {
        component: defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue')),
        props: {
          text: $i18n.t('homepage.city_select.show_citie'),
          style: 'margin:auto;'
        },
        events: {
          click: (val) => {
            redirectToFilterdEvents();
          }
        }
      }
    }
  ];
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/font-size.scss' as *;

.cities {
  margin-top: 40px;
  &__title {
    font-weight: 500;
    @include font-size(30px);
    line-height: 130%;
    margin-bottom: 20px;
    text-align: left;
    margin: 0px 20px;
    @include bp(sm-up) {
      margin: 0px;
      text-align: center;
      margin-bottom: 20px;
    }
  }
  &__cta {
    margin: 40px auto;
    position: relative;
    text-align: center;
  }
}
</style>

<style lang="scss">
.default-modal.modal .modal-header {
  border-bottom: none;
}

.modal-footer {
  box-shadow: 0 -2px 4px -2px rgba(0, 0, 0, 0.1);
  border-top: none;
}
.default-modal.modal .modal-footer {
  flex-direction: column;
}
.default-modal.modal .modal-footer:not(:empty) {
  border-top: none;
}

.modal-container {
  ::-webkit-scrollbar {
    width: 8px; /* Adjust width as needed */
  }

  ::-webkit-scrollbar-track {
    background: var(--color-indigo-50); /* Light pink track */
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--color-indigo-300); /* Bright pink scrollbar */
    border-radius: 10px;
  }

  // ::-webkit-scrollbar-thumb:hover {
  //   background: #ff1493; /* Darker pink on hover */
  // }
}
</style>
